/**
 * Utilities for working with JWT tokens
 */

import { logger } from "@/telemetry";

/**
 * Extracts the expiration timestamp from a JWT access token.
 * JWTs contain an `exp` claim in the payload (in seconds since epoch).
 *
 * @param accessToken - The JWT access token string
 * @returns The expiration timestamp in milliseconds, or null if extraction fails
 */
export function getTokenExpiryFromJWT(accessToken: string): number | null {
  if (!accessToken || typeof accessToken !== "string") {
    return null;
  }

  try {
    const parts = accessToken.split(".");

    if (parts.length !== 3) {
      // Not a valid JWT format (header.payload.signature)
      return null;
    }

    const payload = JSON.parse(window.atob(parts[1]));

    if (typeof payload.exp !== "number") {
      return null;
    }

    // JWT exp is in seconds, convert to milliseconds
    return payload.exp * 1000;
  } catch {
    logger.warn("[tokenUtils] Failed to extract expiry from JWT");

    return null;
  }
}

/**
 * Calculates the token expiry timestamp from a JWT, with a fallback.
 * Use this when sending token info to embedded iframes or external services.
 *
 * @param accessToken - The JWT access token string
 * @param fallbackDurationMs - Fallback duration in ms if JWT parsing fails (default: 1 hour)
 * @returns The expiration timestamp in milliseconds
 */
export function getTokenExpiryWithFallback(
  accessToken: string,
  fallbackDurationMs: number = 60 * 60 * 1000, // 1 hour default
): number {
  const jwtExpiry = getTokenExpiryFromJWT(accessToken);

  if (jwtExpiry !== null) {
    return jwtExpiry;
  }

  // Fallback: assume token expires in the specified duration from now
  return Date.now() + fallbackDurationMs;
}
